// ============================================================
// GEO BLOCKED MODAL - Shows when user is in restricted location
// ============================================================

import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Box,
  Icon,
  Alert,
  AlertIcon,
  AlertDescription,
  Link,
} from '@chakra-ui/react';
import { MapPin, Shield, AlertTriangle, Globe, Wifi } from 'lucide-react';
import PokerButton from '../ui/PokerButton';
import { POKER_COLORS } from '../../theme/designSystem';
import { GeoBlockReason, US_STATES } from '../../types/complianceTypes';

interface GeoBlockedModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason: GeoBlockReason;
  location?: {
    country: string;
    region: string;
    regionCode: string;
    city: string;
  };
  onPlayCoinsOnly?: () => void;
}

const GeoBlockedModal: React.FC<GeoBlockedModalProps> = ({
  isOpen,
  onClose,
  reason,
  location,
  onPlayCoinsOnly,
}) => {
  const getIcon = () => {
    switch (reason) {
      case 'VPN_DETECTED':
      case 'PROXY_DETECTED':
      case 'TOR_DETECTED':
        return Wifi;
      case 'HIGH_RISK_SCORE':
        return AlertTriangle;
      default:
        return MapPin;
    }
  };

  const getTitle = () => {
    switch (reason) {
      case 'RESTRICTED_STATE':
        return 'PSS Coins Not Available in Your State';
      case 'RESTRICTED_COUNTRY':
        return 'PSS Coins Not Available in Your Country';
      case 'VPN_DETECTED':
        return 'VPN Detected';
      case 'PROXY_DETECTED':
        return 'Proxy Detected';
      case 'TOR_DETECTED':
        return 'Tor Network Detected';
      case 'HIGH_RISK_SCORE':
        return 'Connection Flagged';
      default:
        return 'Access Restricted';
    }
  };

  const getMessage = () => {
    switch (reason) {
      case 'RESTRICTED_STATE':
        const stateName = location?.regionCode 
          ? US_STATES[location.regionCode] || location.region 
          : 'your state';
        return (
          <>
            <Text color={POKER_COLORS.accentCyan} textAlign="center" mb={4}>
              Unfortunately, PSS Coins (redeemable currency) is not available in{' '}
              <Text as="span" color={POKER_COLORS.gold} fontWeight="bold">
                {stateName}
              </Text>{' '}
              due to local regulations.
            </Text>
            <Box
              w="100%"
              p={4}
              bg={POKER_COLORS.darkBase}
              borderRadius="lg"
              borderLeft={`4px solid ${POKER_COLORS.cyan}`}
            >
              <Text color={POKER_COLORS.cyan} fontWeight="bold" mb={2}>
                You can still:
              </Text>
              <VStack align="start" spacing={1}>
                <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                   Play with PlayCoins (free, non-redeemable)

                </Text>
                <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                   Participate in PlayCoins tournaments

                </Text>
                <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                   Practice and improve your skills

                </Text>
              </VStack>
            </Box>
          </>
        );

      case 'RESTRICTED_COUNTRY':
        return (
          <Text color={POKER_COLORS.accentCyan} textAlign="center">
            PSS Coins is currently only available in the United States (excluding some states).
            You can still play with PlayCoins for free!
          </Text>
        );

      case 'VPN_DETECTED':
        return (
          <>
            <Text color={POKER_COLORS.accentCyan} textAlign="center" mb={4}>
              We detected that you're using a VPN or similar service. For regulatory
              compliance, we need to verify your actual location.
            </Text>
            <Alert 
              status="warning" 
              bg="rgba(251, 191, 36, 0.2)" 
              borderRadius="md"
            >
              <AlertIcon color="#fbbf24" />
              <AlertDescription color="#fbbf24" fontSize="sm">
                Please disable your VPN and refresh the page to access PSS Coins features.
              </AlertDescription>
            </Alert>
          </>
        );

      case 'PROXY_DETECTED':
        return (
          <Text color={POKER_COLORS.accentCyan} textAlign="center">
            We detected that you're using a proxy server. Please use a direct internet
            connection to access PSS Coins features.
          </Text>
        );

      case 'TOR_DETECTED':
        return (
          <Text color={POKER_COLORS.accentCyan} textAlign="center">
            We detected that you're using the Tor network. Please use a standard browser
            and internet connection to access PSS Coins features.
          </Text>
        );

      case 'HIGH_RISK_SCORE':
        return (
          <>
            <Text color={POKER_COLORS.accentCyan} textAlign="center" mb={4}>
              Your connection has been flagged by our security system. This may be due
              to your network configuration or ISP.
            </Text>
            <Text color={POKER_COLORS.accentCyan} textAlign="center" fontSize="sm">
              If you believe this is an error, please{' '}
              <Link color={POKER_COLORS.cyan} href="mailto:support@pokersweepstars.com">
                contact support
              </Link>.
            </Text>
          </>
        );

      default:
        return (
          <Text color={POKER_COLORS.accentCyan} textAlign="center">
            We couldn't verify your location. Please try again or contact support
            if the issue persists.
          </Text>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent 
        bg={POKER_COLORS.darkTeal} 
        borderColor="#ef4444" 
        border="2px"
        mx={4}
      >
        <ModalHeader color="#ef4444" display="flex" alignItems="center" gap={2}>
          <Icon as={getIcon()} boxSize={6} />
          {getTitle()}
        </ModalHeader>
        <ModalCloseButton color={POKER_COLORS.cyan} />

        <ModalBody>
          <VStack spacing={6} py={4}>
            <Box
              p={4}
              borderRadius="full"
              bg="rgba(239, 68, 68, 0.2)"
            >
              <Icon as={getIcon()} boxSize={12} color="#ef4444" />
            </Box>

            {location && (
              <HStack 
                spacing={2} 
                p={2} 
                bg={POKER_COLORS.darkBase} 
                borderRadius="md"
                fontSize="sm"
              >
                <Icon as={Globe} color={POKER_COLORS.accentCyan} boxSize={4} />
                <Text color={POKER_COLORS.accentCyan}>
                  Detected location: {location.city}, {location.region}, {location.country}
                </Text>
              </HStack>
            )}

            {getMessage()}

            <Box
              w="100%"
              p={3}
              bg="rgba(0, 217, 217, 0.1)"
              borderRadius="lg"
            >
              <HStack>
                <Icon as={Shield} color={POKER_COLORS.cyan} boxSize={5} />
                <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                  This restriction is required to comply with local regulations.
                </Text>
              </HStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter gap={3}>
          {onPlayCoinsOnly && (
            <PokerButton variant="primary" onClick={onPlayCoinsOnly}>
              Play with PlayCoins
            </PokerButton>
          )}
          <PokerButton variant="secondary" onClick={onClose}>
            Close
          </PokerButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GeoBlockedModal;